import { createPinia } from 'pinia'
import { Block2 } from '~/models/Block'
import { Category } from '~/models/Category'
import { Destination2 } from '~/models/Destination'
import { Guide2 } from '~/models/Guide'
import Product, { Activity, PRODUCT_CATEGORY } from '~/models/Product'
import { Site2 } from '~/models/Site'
import { SiteInfo2, SITE_INFO_TYPE } from '~/models/SiteInfo'
import { Spot2 } from '~/models/Spot'
import { DestinationService } from '~/passporter-services/destination/service'
import GuideService from '~/passporter-services/guide/service'
import { ImageService } from '~/passporter-services/shared/image'
import SlugService from '~/passporter-services/shared/slug'
import SiteService from '~/passporter-services/site/service'
import { DestinationNavigation } from '~/services/navigation/destinationNavigation'
import Settings from '~/services/settings'
import { SiteInfoService } from '~/services/siteInfo'
import { TranslationService } from '~/services/Translation'
import { isUuid } from '~/services/uuid'
import { useSiteStore } from '~/store/site'

export class DestinationActions {
	static pinia = createPinia()

	static async getDestination(destinationId: string): Promise<Destination2> {
		try {
			return await DestinationService.getDestination(destinationId)
		} catch (e) {
			console.error(e)
			return {} as Destination2
		}
	}

	static async getSite(siteId: string): Promise<Site2> {
		try {
			const stored: Site2 | undefined = this.siteStore().getSite(siteId)
			if (stored?.id) return stored

			const site = await SiteService.obtainBy(siteId)
			this.siteStore().setSite(site)
			return site
		} catch (e) {
			console.error(e)
			return {} as Site2
		}
	}

	static async getDestinationPath(destinationId: string): Promise<string | undefined> {
		if (!isUuid(destinationId)) return undefined
		const site = await this.getSite(destinationId)
		const destination = site.destination
		if (!destination) return undefined
		return DestinationNavigation.destinationUrl(
			destination.id,
			destination.name,
			destination.countryName
		)
	}

	static async getDestinationSpotPath(
		destinationId: string,
		spotId: string
	): Promise<string | undefined> {
		if (!isUuid(spotId)) return undefined
		const destinationPath = await this.getDestinationPath(destinationId)
		if (!destinationPath) return `/spots/${spotId}`
		return `${destinationPath}/spots/${spotId}`
	}

	static async getDestinationGuidePath(
		destinationId: string,
		guideId: string
	): Promise<string | undefined> {
		if (!isUuid(guideId)) return undefined
		const destinationPath = await this.getDestinationPath(destinationId)
		if (!destinationPath) return `/guides/${guideId}`
		return `${destinationPath}/guides/${guideId}`
	}

	static async getRegionsBlock(siteId: string): Promise<Block2<Destination2>> {
		try {
			const stored: Block2<Destination2>[] | undefined =
				this.siteStore().getSiteDestinationBlocks(siteId)
			if (stored) return stored[0]!
			const destinationBlocks = await SiteService.getDestinationBlocks(siteId)
			this.siteStore().setSiteDestinationBlocks(siteId, destinationBlocks)
			return destinationBlocks[0]!
		} catch (e) {
			console.error(e)
			return {} as Block2<Destination2>
		}
	}

	static async getByIsoCode(iso: string): Promise<Destination2> {
		try {
			return await DestinationService.getByIsoCode(iso)
		} catch (e) {
			console.error(e)
			return {} as Destination2
		}
	}

	static async getGuideCategories(siteId: string): Promise<Category[]> {
		try {
			const stored: Category[] | undefined = this.siteStore().getGuideCategories(siteId)
			if (stored?.length) return stored
			const categories = await GuideService.getCategories(siteId)
			this.siteStore().setGuideCategories(siteId, categories)
			return categories
		} catch (e) {
			console.error(e)
			return []
		}
	}

	static async getGuidesFrom(siteId: string, categoryId?: string): Promise<Guide2[]> {
		try {
			const stored: Guide2[] | undefined = this.siteStore().getGuidesFrom(siteId, categoryId)
			if (stored?.length) return stored
			const guides = await GuideService.getByCategory(siteId, categoryId)
			this.siteStore().setGuidesFrom(siteId, guides, categoryId)
			return guides
		} catch (e) {
			console.error(e)
			return []
		}
	}

	static async getProducts(siteId: string): Promise<Block2<Product>[]> {
		try {
			const stored: Block2<Product>[] | undefined = this.siteStore().getSiteProducts(siteId)
			if (stored?.length) return stored

			const products = await SiteService.getProducts(siteId)
			this.siteStore().setSiteProducts(siteId, products)

			return products
		} catch (e) {
			console.error(e)
			return []
		}
	}

	static async getTrips(siteId: string): Promise<Product[]> {
		try {
			const stored: Product[] | undefined = this.siteStore().getTrips(siteId)
			if (stored?.length) return stored

			const trips = await SiteService.getProductsByCategory(siteId, PRODUCT_CATEGORY.TRIP)
			this.siteStore().setTrips(siteId, trips)

			return trips
		} catch (e) {
			console.error(e)
			return []
		}
	}

	static async getActivities(siteId: string, destinationId: string): Promise<Activity[]> {
		try {
			const isExludedDestination: boolean =
				this.isExcludedDestinationToDisplayActivities(destinationId)
			if (isExludedDestination) return []
			const stored: Activity[] | undefined = this.siteStore().getActivities(siteId)
			if (stored?.length) return stored

			const activities = await DestinationService.getActivities(destinationId)
			this.siteStore().setActivities(siteId, activities)

			return activities
		} catch (e) {
			console.error(e)
			return []
		}
	}

	static async getRecommendedSpots(siteId: string): Promise<Spot2[]> {
		try {
			const stored: Spot2[] | undefined = this.siteStore().getRecommendedSpots(siteId)
			if (stored?.length) return stored
			const spots = await SiteService.getRecommendedSpots(siteId)
			this.siteStore().setRecommendedSpots(siteId, spots)
			return spots
		} catch (e) {
			console.error(e)
			return []
		}
	}

	static setSiteMeta(site: Site2, i18n: any) {
		if (!site?.destination) return
		const route = useRoute()
		const destination = site.destination
		const pageUrl = `${Settings.baseUrl()}${route.fullPath}`
		const discoverDestination = i18n.t('microsite_metatitle', {
			destination: destination.name,
		})
		useHead(this.obtainMetaInfo(discoverDestination, pageUrl, destination))
	}

	static async getSiteInfo(siteId: string): Promise<SiteInfo2[]> {
		try {
			const stored: SiteInfo2[] | undefined = this.siteStore().getSiteInfo(siteId)
			if (stored?.length) return stored
			const info = await SiteService.obtainInfo(siteId)
			const sorted = SiteInfoService.sortByType(info)
			this.siteStore().setSiteInfo(siteId, sorted)
			return sorted
		} catch (e) {
			console.error(e)
			return []
		}
	}

	static redirectProperlyInfo(locale: string, type: SITE_INFO_TYPE, destinationName: string): void {
		const service = new SiteInfoService(locale, destinationName)
		const url = service.urlCorrelation()[type]
		DestinationNavigation.openExternalNavigationTo(url!)
	}

	static obtainIconsInfo(type: SITE_INFO_TYPE): string {
		return this.INFO_ICONS[type]
	}

	private static siteStore() {
		return useSiteStore()
	}

	private static getCanonicalUrl(
		destinationName: string,
		destinationId: string,
		countryName?: string,
		locale?: string
	): string {
		const localePath = locale ?? TranslationService.getInstance().getLocale()
		const nameSlug: string = SlugService.normalize(destinationName)
		const baseUrl = `${Settings.baseUrl()}/${localePath}/destinations`
		let canonical = `${baseUrl}/${nameSlug}/${destinationId}`
		if (countryName) {
			const countrySlug: string = SlugService.normalize(countryName)
			canonical = `${baseUrl}/${countrySlug}/${nameSlug}/${destinationId}`
		}
		return canonical
	}

	private static getLocalizedUrls(destination: Destination2): Record<string, string>[] {
		const localizedNames = destination.localizedName
		const localizedCountry = destination.localizedCountryName
		const supportedLangs = ['ca', 'de', 'en', 'es', 'fr', 'it', 'pt']

		if (!localizedNames) return []
		const translations = Object.entries(localizedNames).filter(([locale]) =>
			supportedLangs.includes(locale)
		)

		const localizedLinks = translations.map(([locale, localizedName]) => {
			const localizedCountryName = localizedCountry?.[locale]
			return {
				rel: 'alternate',
				hreflang: locale,
				href: this.getCanonicalUrl(localizedName, destination.id, localizedCountryName, locale),
			}
		})

		return localizedLinks
	}

	private static obtainMetaInfo(
		discoverDestination: string,
		pageUrl: string,
		destination: Destination2
	) {
		const cover = ImageService.resizeImage(destination.cover, 1000, 1000)
		const description: string = `${discoverDestination}. ${destination.description || ''}`
		return {
			title: discoverDestination,
			meta: [
				{
					name: 'description',
					content: description.trim(),
				},
				{ property: 'og:title', content: discoverDestination },
				{ property: 'og:description', content: destination.description },
				{ property: 'og:image', content: cover },
				{ property: 'og:url', content: pageUrl },
				{ property: 'og:type', content: 'website' },
			],
			link: [
				{
					rel: 'canonical',
					href: this.getCanonicalUrl(destination.name, destination.id, destination.countryName),
				},
				...this.getLocalizedUrls(destination),
			],
		}
	}

	private static readonly INFO_ICONS = {
		[SITE_INFO_TYPE.DOCUMENTATION]: 'chip',
		[SITE_INFO_TYPE.MONEY]: 'money',
		[SITE_INFO_TYPE.GASTRONOMY]: 'gastronomy',
		[SITE_INFO_TYPE.TRANSPORT]: 'bus',
		[SITE_INFO_TYPE.CLIMATE]: 'cloud_sun',
		[SITE_INFO_TYPE.HEALTH_AND_SAFETY]: 'lock',
		[SITE_INFO_TYPE.CONNECTIVITY]: 'wifi',
		[SITE_INFO_TYPE.CULTURE]: 'book_save',
		[SITE_INFO_TYPE.SOUVENIRS]: 'gift',
	}

	private static isExcludedDestinationToDisplayActivities(destinationId: string): boolean {
		const excludedDestinations: string[] = ['80ec7e43-8a4f-4f16-9a17-305dfb43e0aa']
		return excludedDestinations.includes(destinationId)
	}
}
